import { useEffect } from "react";

import { logInfoWithDebugFlag } from "../../utils/logger";

import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCourses,
  selectCurrentCourse,
  selectCurrentUserId,
  selectWebsocketUserEmail,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector, usePromiseEffect } from "store/store-hooks";
import { trackUserProperty } from "utils/amplitude";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { Outlet, useLocation, useNavigate, useParams } from "utils/routing";
import { getDefaultCourse } from "utils/section";
import { courseHomeUrl } from "utils/urls";
import { getWindowId } from "utils/window";

export default function Layout() {
  const currentCourse = useAppSelector(selectCurrentCourse);
  const courses = useListSelector(selectCourses);
  const userId = useAppSelector(selectCurrentUserId);
  const currentCourseId = currentCourse?.id;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const homeUrl = useHomeUrl();

  useEffect(() => {
    // There might be a case where we briefly don't have a current course
    if (currentCourse) {
      const state = store.getState();

      // TODO: We're seeing this message on the course monitoring dashboard.
      logInfoWithDebugFlag("debug_pubsub", `Attempting to set new section version`, {
        section_version: currentCourse?.section_version,
        course_id: currentCourse?.id,
        email: selectWebsocketUserEmail(state),
        windowId: getWindowId(),
        userId: selectCurrentUserId(state),
      });

      dispatch(viewStateActions.setSectionVersionTo(currentCourse?.section_version));
    }
  }, [currentCourse, dispatch]);

  // Ensure this component refreshes when we navigate between the course routes
  const pathParams = useParams();
  const routeCourseId = pathParams.courseId;

  const { isLoading, data } = usePromiseEffect(() => {
    if (userId) {
      return storeApi.courses.list({}, { skipToast: true });
    }
    return Promise.resolve(null);
  }, [userId]);

  useEffect(() => {
    if (!userId || isLoading || !data) {
      return;
    }

    if (courses.length === 0) {
      navigate(homeUrl);
      return;
    }

    if (!routeCourseId || !courses.some((c) => c.id === routeCourseId)) {
      const newRouteCourseId: string = currentCourseId || getDefaultCourse(courses).id;

      navigate(courseHomeUrl(newRouteCourseId));
      return;
    }

    if (routeCourseId && currentCourseId !== routeCourseId) {
      dispatch(viewStateActions.setCurrentCourseId(routeCourseId));
      trackUserProperty("courseId", routeCourseId);
    }
  }, [
    routeCourseId,
    currentCourseId,
    location,
    dispatch,
    navigate,
    homeUrl,
    isLoading,
    data,
    courses,
    userId,
  ]);

  return <Outlet />;
}
